import { useState } from "react";
import type { TenantToolCallHistory } from "../../hooks/useAdminStatus";
import {
  ChartTooltip,
  tooltipStyles as tip,
  type ChartTooltipAnchor,
} from "./chart/ChartTooltip";
import { axisTicks, bucketRangeLabel, niceAxisMax, toolCallTooltipLine } from "../../lib/chartBuckets";
import styles from "./ToolCallsChart.module.css";

const AXIS_W = 34;

type Hover = ChartTooltipAnchor & { readonly index: number };

// Tool calls per bucket, ok (green) stacked under error (red). Same 48-bar
// geometry as UsageChart so the two charts line up, plus a left y-axis and a
// portaled hover tooltip.
export const ToolCallsChart = ({ history }: { history: TenantToolCallHistory }): JSX.Element => {
  const [hover, setHover] = useState<Hover | null>(null);
  const totals = history.points.map((p) => p.ok + p.error);
  const axisMax = niceAxisMax(Math.max(...totals, 1));
  const width = 480;
  const height = 120;
  const barGap = 2;
  const usable = height - 14;
  const plotW = width - AXIS_W;
  const barWidth = (plotW - barGap * (history.points.length - 1)) / history.points.length;
  const ticks = axisTicks(axisMax, 3);
  const onEnter = (index: number) => (e: React.MouseEvent<SVGRectElement>): void => {
    const r = e.currentTarget.getBoundingClientRect();
    setHover({ index, cx: r.left + r.width / 2, top: r.top, bottom: r.bottom });
  };
  const hovered = hover != null ? history.points[hover.index] : undefined;
  return (
    <>
      <svg
        className={styles.chart}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label="Tool calls per bucket"
      >
        {ticks.map((t) => {
          const y = height - (t / axisMax) * usable;
          if (t === 0) return null;
          return (
            <line
              key={t}
              className={styles.gridline}
              x1={AXIS_W}
              x2={width}
              y1={y}
              y2={y}
              aria-hidden
            />
          );
        })}
        <text className={styles.axisLabel} x={0} y={10} aria-hidden>
          {Math.round(axisMax)}
        </text>
        <text className={styles.axisLabel} x={0} y={height - 1} aria-hidden>
          0
        </text>
        {history.points.map((p, i) => {
          const x = AXIS_W + i * (barWidth + barGap);
          const okH = (p.ok / axisMax) * usable;
          const errH = (p.error / axisMax) * usable;
          return (
            <g key={p.t}>
              <rect
                className={styles.hit}
                x={x}
                y={0}
                width={barWidth}
                height={height}
                aria-label={`${bucketRangeLabel(p.t, history.bucketSeconds)}: ${toolCallTooltipLine(p.ok, p.error)}`}
                onMouseEnter={onEnter(i)}
                onMouseLeave={() => setHover(null)}
              />
              {p.ok > 0 && (
                <rect className={styles.ok} x={x} y={height - okH} width={barWidth} height={okH} rx={1} />
              )}
              {p.error > 0 && (
                <rect
                  className={styles.error}
                  x={x}
                  y={height - okH - errH}
                  width={barWidth}
                  height={errH}
                  rx={1}
                />
              )}
            </g>
          );
        })}
      </svg>
      {hover != null && hovered != null && (
        <ChartTooltip anchor={hover}>
          <div className={tip.tipHead}>
            <span className={tip.tipState}>Tool calls</span>
            <span className={tip.tipTime}>{bucketRangeLabel(hovered.t, history.bucketSeconds)}</span>
          </div>
          <div className={tip.tipBody}>{toolCallTooltipLine(hovered.ok, hovered.error)}</div>
        </ChartTooltip>
      )}
    </>
  );
};
